import {useNavigate} from 'react-router-dom';
import {Building2, MapPin, Plus} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {RouterInput, RouterOutput, api, trpc} from '@common-web/lib/trpc';
import {STALE_TIME_5_MIN} from '@common/constants/time';
import {IN_HOUSE_ADMINISTRATOR} from '@common/constants/administrators';
import {formatCityStateZip, formatDate} from '@common/lib/utils';
import {useIsInternal} from '@common-web/providers/portalProvider';
import {ROUTES} from '@client/lib/routes';
import {
  DataTable,
  useDataTable,
  type Column,
  type DataTableFilter,
} from '@common-web/components/data-table';

type GroupResult = RouterOutput['vba']['groups']['groupDetails']['search']['groups'][number];
type GroupSortKey = NonNullable<RouterInput['vba']['groups']['groupDetails']['search']['sortKey']>;

type GroupFilters = {includeDisenrolled: boolean; clientId: string; administeredBy: string};

const groupColumns: Column<GroupResult>[] = [
  {
    id: 'group',
    header: 'Group',
    sortable: true,
    sortKey: 'name',
    render: (item) => (
      <div className='flex items-start gap-3'>
        <div className='p-2 bg-brand-blue/10 rounded-lg'>
          <Building2 className='h-4 w-4 text-brand-blue' />
        </div>
        <div>
          <div className='font-semibold text-brand-navy text-base'>{item.name}</div>
          <div className='text-sm text-brand-gray mt-0.5'>
            <span className='font-medium'>ID:</span> {item.groupId}
          </div>
        </div>
      </div>
    ),
    csv: {value: (item) => item.name},
  },
  {
    id: 'administeredBy',
    header: 'Administered by',
    sortable: true,
    render: (item) =>
      item.externalTpa ? (
        <Badge className='bg-violet-50 text-violet-800 border-violet-200 font-medium'>
          {item.externalTpaLabel}
        </Badge>
      ) : (
        <Badge className='bg-brand-blue/10 text-brand-blue border-brand-blue/20 font-medium'>
          {IN_HOUSE_ADMINISTRATOR.label}
        </Badge>
      ),
    csv: {
      value: (item) => (item.externalTpa ? item.externalTpaLabel : IN_HOUSE_ADMINISTRATOR.label),
    },
  },
  {
    id: 'location',
    header: 'Location',
    sortable: true,
    render: (item) => {
      const cityStateZip = formatCityStateZip(
        item.address.city,
        item.address.state,
        item.address.zipCode,
      );
      if (!item.address.line1 && !cityStateZip) {
        return <span className='text-sm text-brand-gray'>—</span>;
      }
      return (
        <div className='text-sm space-y-1'>
          {item.address.line1 && <div className='text-brand-navy'>{item.address.line1}</div>}
          {cityStateZip && (
            <div className='flex items-center gap-1.5 text-brand-gray'>
              <MapPin className='h-3.5 w-3.5' />
              {cityStateZip}
            </div>
          )}
        </div>
      );
    },
    csv: {
      value: (item) =>
        [
          item.address.line1,
          formatCityStateZip(item.address.city, item.address.state, item.address.zipCode),
        ]
          .filter(Boolean)
          .join(', '),
    },
  },
  {
    csv: {value: (item) => formatDate(item.originalDate, 'MM/DD/YYYY', 'N/A')},
    header: 'Start Date',
    id: 'startDate',
    render: (item) => (
      <div className='text-sm space-y-1'>
        <div className='text-brand-navy font-medium'>
          {formatDate(item.originalDate, 'MM/DD/YYYY', 'N/A')}
        </div>
        {item.disenrollDate && (
          <div className='text-brand-gray'>
            Disenrolled: {formatDate(item.disenrollDate, 'MM/DD/YYYY')}
          </div>
        )}
      </div>
    ),
    sortable: true,
  },
  {
    align: 'right',
    csv: {value: (item) => item.activePrimaryMembers ?? ''},
    header: 'Active Primary Members',
    id: 'activePrimaryMembers',
    render: (item) => (
      <span className='text-sm font-medium text-brand-navy'>
        {item.activePrimaryMembers?.toLocaleString() ?? '—'}
      </span>
    ),
    sortable: true,
  },
  {
    id: 'status',
    header: 'Status',
    sortable: true,
    render: (item) =>
      item.isActive ? (
        <Badge className='bg-emerald text-white border-0 font-semibold px-3 py-1'>Active</Badge>
      ) : (
        <Badge className='bg-brand-gray text-white border-0 font-semibold px-3 py-1'>
          Disenrolled
        </Badge>
      ),
    csv: {value: (item) => (item.isActive ? 'Active' : 'Disenrolled')},
  },
];

export const GroupList = () => {
  const isInternal = useIsInternal();
  const navigate = useNavigate();

  const table = useDataTable<GroupFilters>({
    defaultPageSize: 20,
    defaultFilters: {includeDisenrolled: false, clientId: '', administeredBy: ''},
    searchDebounce: 900,
  });

  // External third-party administrator groups are only visible to the tenant's own admins. This endpoint
  // returns an empty list for everyone else, which is what hides the filter — the
  // entitlement rule stays on the server rather than being mirrored here.
  const externalTpasQuery = trpc.vba.members.listExternalTpas.useQuery(undefined, {
    staleTime: STALE_TIME_5_MIN,
  });
  const externalTpasVisible = (externalTpasQuery.data?.length ?? 0) > 0;

  // Drop the column when there is only one administrator in play — it would read
  // "CBS" on every row. That covers the internal portal, which never sees
  // external-TPA rows, and any tenant without external administrators.
  const columns = externalTpasVisible
    ? groupColumns
    : groupColumns.filter((column) => column.id !== 'administeredBy');
  // Options come from the same response rather than a list written here: which
  // external TPAs a tenant has is the registry's answer, so adding one on the server
  // is the whole change. CBS is prepended because it is the absence of an external
  // administrator and so has no adapter to be registered in.
  const administeredByOptions = [IN_HOUSE_ADMINISTRATOR, ...(externalTpasQuery.data ?? [])].map(
    (administrator) => ({value: administrator.code, label: administrator.label}),
  );

  let clientsData: RouterOutput['internal']['client']['list'] = [];
  if (isInternal) {
    const {data: cd} = trpc.internal.client.list.useQuery();
    clientsData = cd || [];
  }

  const groupFilters: DataTableFilter<GroupFilters>[] = [
    ...(isInternal
      ? [
          {
            key: 'clientId' as const,
            label: 'Client',
            type: 'select' as const,
            options: clientsData.map((c: RouterOutput['internal']['client']['list'][number]) => ({
              value: c.id,
              label: c.name,
            })),
          },
        ]
      : []),
    ...(externalTpasVisible
      ? [
          {
            key: 'administeredBy' as const,
            label: 'Administered by',
            type: 'select' as const,
            options: administeredByOptions,
          },
        ]
      : []),
    {key: 'includeDisenrolled' as const, label: 'Show disenrolled', type: 'switch' as const},
  ];

  const sort = {
    sortDirection: table.sort?.direction,
    sortKey: table.sort?.key as GroupSortKey | undefined,
  };

  const {data, isLoading, isFetching, error} = trpc.vba.groups.groupDetails.search.useQuery({
    page: table.page,
    pageSize: table.pageSize,
    clientId: table.filterValues.clientId || undefined,
    search: table.debouncedSearch || undefined,
    includeDisenrolled: table.filterValues.includeDisenrolled,
    administeredBy: table.filterValues.administeredBy || undefined,
    ...sort,
  });

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        <PageHeader
          title='Groups'
          description='Search and manage groups'
          icon={<Building2 className='h-6 w-6' />}
          isFetching={isFetching}
          isLoading={isLoading}
        >
          {/* {isInternal && (
            <PageHeaderAction onClick={() => setIsCreateModalOpen(true)}>
              <Plus className='h-4 w-4 mr-2' />
              Create Group
            </PageHeaderAction>
          )} */}
        </PageHeader>

        {/* Main Content Area */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          <DataTable<
            GroupResult,
            {includeDisenrolled: boolean; clientId: string; administeredBy: string}
          >
            table={table}
            columns={columns}
            data={data?.groups}
            total={data?.total}
            totalPages={data?.totalPages}
            loading={isLoading}
            error={error}
            emptyMessage='No groups found'
            title='Groups List'
            entity={{singular: 'group', plural: 'groups'}}
            searchPlaceholder='Search by group ID or name...'
            filters={groupFilters}
            csvExport={{
              filename: 'groups',
              getAllRows: async () => {
                // The search endpoint caps pageSize at 100, so page through all
                // results to build the full CSV.
                const CSV_PAGE_SIZE = 100;
                const allGroups: GroupResult[] = [];
                let csvPage = 1;
                let totalPages = 1;
                do {
                  const r = await api.vba.groups.groupDetails.search.query({
                    page: csvPage,
                    pageSize: CSV_PAGE_SIZE,
                    clientId: table.filterValues.clientId || undefined,
                    search: table.debouncedSearch || undefined,
                    includeDisenrolled: table.filterValues.includeDisenrolled,
                    administeredBy: table.filterValues.administeredBy || undefined,
                    ...sort,
                  });
                  allGroups.push(...r.groups);
                  totalPages = r.totalPages;
                  csvPage += 1;
                } while (csvPage <= totalPages);
                return allGroups;
              },
            }}
            rowKey={(g) => `${g.externalTpa ?? IN_HOUSE_ADMINISTRATOR.code}-${g.groupId}`}
            rowHref={(g) =>
              g.externalTpa
                ? ROUTES.externalTpaGroup.to(g.externalTpa, g.groupId)
                : `/groups/${g.groupId}`
            }
          />
        </div>
      </div>
    </div>
  );
};
